<div class="published-container">
    <!-- Header Section -->
    <header class="published-header">
        <div class="header-content">
            <div class="title-section">
                <h1 class="page-title">Published Templates</h1>
                <p class="page-subtitle">Manage your AWS SES email templates</p>
            </div>
            <div class="header-controls">
                <div class="search-wrapper">
                    <mat-icon class="search-icon">search</mat-icon>
                    <input type="text" [(ngModel)]="searchQueryValue" (ngModelChange)="onSearchChange()"
                        placeholder="Search templates..." class="search-input" aria-label="Search templates"
                        [disabled]="isLoading" />
                    @if (searchQueryValue) {
                    <button class="clear-btn" (click)="clearSearch()" matTooltip="Clear search"
                        aria-label="Clear search">
                        <mat-icon>close</mat-icon>
                    </button>
                    }
                </div>
                <button class="btn-refresh" (click)="refreshTemplates()" [disabled]="isLoading || isRefreshing()"
                    [class.loading]="isLoading || isRefreshing()" matTooltip="Refresh from AWS SES"
                    aria-label="Refresh templates from AWS SES">
                    <mat-icon [class.spinning]="isLoading || isRefreshing()">sync</mat-icon>
                    <span class="btn-text">Refresh</span>
                </button>
            </div>
        </div>
    </header>

    <!-- Loading State -->
    @if (isLoading) {
    <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading templates from AWS SES...</p>
    </div>
    } @else {
    <!-- Main Content -->
    <main class="published-content">
        @if (filteredTemplates.length > 0) {
        <!-- Desktop Table View -->
        <div class="table-container desktop-view">
            <table class="templates-table">
                <thead>
                    <tr>
                        <th class="col-name">Template Name</th>
                        <th class="col-lang">Languages</th>
                        <th class="col-created-by">Published By</th>
                        <th class="col-date">Published</th>
                        @if (isAdmin) {
                        <th class="col-actions">Actions</th>
                        }
                    </tr>
                </thead>
                <tbody>
                    @for (template of filteredTemplates; track trackByBaseName($index, template)) {
                    <tr class="template-row">
                        <td class="name-cell">
                            <div class="template-name-wrapper" (click)="copyTemplateName(template.baseName, $event)"
                                matTooltip="Click to copy template ID">
                                <mat-icon class="template-icon copy-icon">content_copy</mat-icon>
                                <span class="template-name">{{ template.baseName }}</span>
                            </div>
                        </td>
                        <td class="lang-cell">
                            <div class="language-badges">
                                @for (lang of template.languages; track trackByLangCode($index, lang)) {
                                <button class="language-badge" [attr.data-lang]="lang.languageCode"
                                    [class.no-content]="!lang.hasContent"
                                    (click)="previewLanguage(template, lang)"
                                    [matTooltip]="lang.hasContent ? 'Preview ' + (lang.languageCode | uppercase) + ' version' : (lang.languageCode | uppercase) + ' - No content available'">
                                    {{ lang.languageCode | uppercase }}
                                </button>
                                }
                            </div>
                        </td>
                        <td class="created-by-cell">
                            <span class="created-by-value">{{ fbs.getUserNameFromId(template.publishedBy) || '—' }}</span>
                        </td>
                        <td class="date-cell">
                            @if (isValidDate(template.publishedTimestamp)) {
                            <span class="date-value">{{ template.publishedTimestamp | dfnsFormatDistanceToNowStrict }}
                                ago</span>
                            } @else {
                            <span class="date-empty">—</span>
                            }
                        </td>
                        @if (isAdmin) {
                        <td class="actions-cell">
                            <div class="action-buttons">
                                <button class="btn-action btn-json"
                                    (click)="viewTemplateJson(template); $event.stopPropagation()"
                                    [disabled]="loadingJsonFor() === template.baseName"
                                    [matTooltip]="loadingJsonFor() === template.baseName ? 'Loading...' : 'View JSON structure'"
                                    aria-label="View template JSON">
                                    @if (loadingJsonFor() === template.baseName) {
                                    <mat-spinner diameter="18"></mat-spinner>
                                    } @else {
                                    <mat-icon>data_object</mat-icon>
                                    }
                                </button>
                                <button class="btn-action btn-delete"
                                    (click)="deleteTemplate(template); $event.stopPropagation()"
                                    matTooltip="Delete all language versions" aria-label="Delete template">
                                    <mat-icon>delete_outline</mat-icon>
                                </button>
                            </div>
                        </td>
                        }
                    </tr>
                    }
                </tbody>
            </table>
        </div>

        <!-- Mobile/Tablet Card View -->
        <div class="cards-container mobile-view">
            @for (template of filteredTemplates; track trackByBaseName($index, template)) {
            <article class="template-card">
                <div class="card-header">
                    <div class="card-title-row" (click)="copyTemplateName(template.baseName, $event)"
                        matTooltip="Tap to copy template ID">
                        <mat-icon class="card-icon copy-icon">content_copy</mat-icon>
                        <h3 class="card-name">{{ template.baseName }}</h3>
                    </div>
                </div>
                <div class="card-body">
                    <div class="card-languages">
                        @for (lang of template.languages; track trackByLangCode($index, lang)) {
                        <button class="language-badge" [attr.data-lang]="lang.languageCode"
                            [class.no-content]="!lang.hasContent"
                            (click)="previewLanguage(template, lang)"
                            [matTooltip]="lang.hasContent ? 'Preview ' + (lang.languageCode | uppercase) + ' version' : (lang.languageCode | uppercase) + ' - No content available'">
                            {{ lang.languageCode | uppercase }}
                        </button>
                        }
                    </div>
                    <div class="card-meta">
                        <mat-icon class="meta-icon">person</mat-icon>
                        <span>{{ fbs.getUserNameFromId(template.publishedBy) || 'Unknown' }}</span>
                    </div>
                    <div class="card-meta">
                        <mat-icon class="meta-icon">schedule</mat-icon>
                        @if (isValidDate(template.publishedTimestamp)) {
                        <span>{{ template.publishedTimestamp | dfnsFormatDistanceToNowStrict }} ago</span>
                        } @else {
                        <span>No date available</span>
                        }
                    </div>
                </div>
                @if (isAdmin) {
                <div class="card-footer">
                    <button class="btn-card btn-card-json"
                        (click)="viewTemplateJson(template); $event.stopPropagation()">
                        <mat-icon>data_object</mat-icon>
                        <span>JSON</span>
                    </button>
                    <button class="btn-card btn-card-delete"
                        (click)="deleteTemplate(template); $event.stopPropagation()">
                        <mat-icon>delete_outline</mat-icon>
                        <span>Delete</span>
                    </button>
                </div>
                }
            </article>
            }
        </div>

        <!-- Results Footer -->
        <footer class="results-footer">
            <div class="results-count">
                <mat-icon class="count-icon">folder_open</mat-icon>
                <span>Showing <strong>{{ filteredTemplates.length }}</strong> templates
                    (<strong>{{ filteredTemplateCount }}</strong> language versions)</span>
            </div>
        </footer>
        } @else {
        <!-- Empty State -->
        <div class="empty-state">
            <div class="empty-content">
                @if (searchQueryValue) {
                <div class="empty-icon-wrapper search">
                    <mat-icon class="empty-icon">search_off</mat-icon>
                </div>
                <h2 class="empty-title">No matching templates</h2>
                <p class="empty-description">We couldn't find any templates matching "<strong>{{ searchQueryValue
                        }}</strong>"</p>
                <button class="btn-empty" (click)="clearSearch()">
                    <mat-icon>clear</mat-icon>
                    <span>Clear Search</span>
                </button>
                } @else {
                <div class="empty-icon-wrapper">
                    <mat-icon class="empty-icon">drafts</mat-icon>
                </div>
                <h2 class="empty-title">No published templates</h2>
                <p class="empty-description">Templates published to AWS SES will appear here.<br>Click refresh to sync
                    with AWS SES.</p>
                <button class="btn-empty" (click)="refreshTemplates()" [disabled]="isLoading || isRefreshing()">
                    <mat-icon [class.spinning]="isLoading || isRefreshing()">sync</mat-icon>
                    <span>Refresh from AWS SES</span>
                </button>
                }
            </div>
        </div>
        }
    </main>
    }
</div>
